  <template>
    <div class='configuration-layout'>
      <div class="top-section">
        <div class='domain-organization-filters'>
          <advanced-filters-header addDomainEnabled layout='configuration' @showAddDomainPopup='showPopup = true'
            @applyDomains='apply' />
        </div>
        <div class='logo-ai' v-if='this.activeName === "leaks"'>
          <div class='logo-ai__wrapper'>
            <company-logo :domain="whiteLabelingConfig.domain" view="ai" v-if="whiteLabelingConfig.domain" />
            <img :src="activeAiLogo" class='logo-ai__logo' v-else />
            <div class='logo-ai__text'>Powered by</div>
          </div>
        </div>
        <div class='widget-section'>
          <geo-locations-widget v-if="activeName === 'infrastructure'" :locations='locationsForIPs'
            @openGeoLocationsPopup='openGeoLocationsPopup' />
          <infostealers-keywords-widget v-if="activeName === 'infostealers'" :keywords='runningKeywordsForWidget' />
        </div>
      </div>
      <add-new-item-popup v-model="showPopup" :is-for-domain='true' :title="$t('configuration.add.domain.title')"
        :label="$t('configuration.domain.label')" :add-button-title="$t('configuration.add.domain')"
        :validation-rules="[validationRules.domain]" @add="addNewDomains" />
      <add-tags-popup v-model="showAddTagsPopup" :assets='assetsForTagPopup' :isForCloudAccount='isForCloudAccount'
        :isForInfrastructure="isForInfrastructure" @toggleTagsPopup='closeTagsPopup'
        @submitTagsInformation='submitTagsInformation' />
      <yes-no-popup :visible-popup="showTagsApprovalPopup" title="add.tags.popup.title" message="add.tags.popup.message"
        yesCaption="confirm.ok" noCaption="confirm.cancel" :show-no-button="false"
        @clicked="onAddTagsClickYesNoButton" />
      <g-e-o-locations-popup v-model="showGeoLocationsPopup" :visible-popup='showGeoLocationsPopup'
        @closeClicked='showGeoLocationsPopup = false' :locations='locationsForIPs' />
      <el-tabs class='tabs-section' v-model="activeName" @tab-click="handleClick">
        <el-tab-pane :label="$t('configuration.tld.label')" name="topLevelDomain">
          <top-level-domains-tab :current-tab='activeName' :actionsWidth='sizeOfActionsTLDTab'
            :amountOfTestCardInRow='amountOfTestCardInRow'
            :forceResetSelectedItemsTopLevelDomain="forceResetSelectedItemsTopLevelDomain"
            :forceLoadTopLevelDomain="forceLoadTopLevelDomain" @toggleTagsPopup='toggleTagsPopup'
            @submitTagsInformation='submitTagsInformation' @apply='apply'
            @forceRerenderTopLevelDomainTab='forceRerenderTopLevelDomainTab' />
        </el-tab-pane>
        <el-tab-pane :label="$t('configuration.cloud.accounts.label')" name="cloud_accounts">
          <cloud-accounts-tab :current-tab='activeName' :accounts-selected="accountsToPass"
            :enableCloudComplianceFeature='cloudComplianceFeatureOn' @apply='apply' @toggleTagsPopup='toggleTagsPopup'
            @submitTagsInformation='submitTagsInformation' />
        </el-tab-pane>
        <el-tab-pane :label="$t('configuration.infrastructure.assets.label')" name="infrastructure">
          <infrastructure-tab :current-tab='activeName' :has-filter='hasFilter'
            :amountOfTestCardInRow='amountOfTestCardInRow'
            :forceResetSelectedItemsInfrastructure="forceResetSelectedItemsInfrastructure"
            :forceLoadInfrastructure="forceLoadInfrastructure" @submitTagsInformation='submitTagsInformation'
            @toggleTagsPopup='toggleTagsPopup' @forceRerenderTopLevelDomainTab='forceRerenderTopLevelDomainTab' />
        </el-tab-pane>
        <el-tab-pane :label="$t('configuration.cloud.assets.label')" name="cloud">
          <cloud-assets-tab :amountOfTestCardInRow='amountOfTestCardInRow'
            :enableCloudComplianceFeature='cloudComplianceFeatureOn' :current-tab='activeName' />
        </el-tab-pane>
        <el-tab-pane :label="$t('configuration.employees.label')" name="employees">
          <employees-tab :current-tab='activeName' :has-filter='hasFilter' />
        </el-tab-pane>
        <el-tab-pane :label="$t('configuration.infostealers.label')" name="infostealers">
          <infostealers-tab :current-tab='activeName' />
        </el-tab-pane>
        <el-tab-pane :label="$t('configuration.technologies.label')" name="technologies">
          <technologies-tab :current-tab='activeName' :has-filter='hasFilter' />
        </el-tab-pane>
        <el-tab-pane :label="$t('configuration.leaks.label')" name="leaks">
          <leaks-tab :has-filter='hasFilter' :current-tab='activeName' />
        </el-tab-pane>
      </el-tabs>
      <success-operation-message :message='successMessage' @close="clearSuccessMessage" />
      <error-message :errorText="errorMessage" @close="errorMessage = ''" />
    </div>
  </template>

<script>
import { mapGetters } from 'vuex';
import {
  ADD_URL_CONFIGURATIONS, CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS,
  LOAD_AMOUNT_OF_RUNNING_PROJECTS, LOAD_CLOUD_ACCOUNT_TAGS_FROM_CONFIGURATION,
  LOAD_DOMAIN_TAGS_FROM_CONFIGURATION,
  LOAD_DOMAINS_FROM_CONFIGURATION,
  CALCULATE_LAST_SCAN_DATA,
  LOAD_ORGANIZATION_TAGS_FROM_CONFIGURATION,
} from '@/store/project/actionTypes';
import { LOAD_DOMAINS, GET_SYSTEM_SETTINGS } from '@/store/user/actionTypes';
import { CONFIGURATION_ACTION_TYPES, CONFIGURATION } from '@/constants/configuration';
import { rules } from '@/utils/validationUtils';
import {
  calculateProgressValue,
  calculateState,
  calculateAmount,
  configuredKeywords,
} from '@/utils/infostealersProgress';

import AddNewItemPopup from '@/components/popup/AddNewItemPopup.vue';
import EmployeesTab from '@/components/configuration/EmployeesTab.vue';
import TopLevelDomainsTab from '@/components/configuration/TopLevelDomainsTab.vue';
import InfrastructureTab from '@/components/configuration/InfrastructureTab.vue';
import CloudAssetsTab from '@/components/configuration/CloudAssetsTab.vue';
import TechnologiesTab from '@/components/configuration/TechnologiesTab.vue';
import LeaksTab from '@/components/configuration/LeaksTab.vue';
import InfostealersTab from '@/components/configuration/InfostealersTab.vue';
import CynergyAppAi from '@/assets/icons/whitelabeling/cynergy.app/cynai-logo-black.svg';
import CynergyAppAiWhite from '@/assets/icons/whitelabeling/cynergy.app/cynai-logo-white.svg';
import CompanyLogo from '@/components/common/CompanyLogo.vue';
import AddTagsPopup from '@/components/popup/AddTagsPopup.vue';
import {
  RESET_SUCCESS_OPERATION_MESSAGE,
  TOGGLE_DOWNLOAD_REPORT_DISABLE,
  START_INFOSTEALERS_POLLING,
  STOP_INFOSTEALERS_POLLING,
} from '@/store/configuration/actionTypes';
import SuccessOperationMessage from '@/components/common/SuccessOperationMessage.vue';
import GeoLocationsWidget from '@/components/configuration/GeoLocationsWidget.vue';
import InfostealersKeywordsWidget from '@/components/configuration/InfostealersKeywordsWidget.vue';
import GEOLocationsPopup from '@/components/popup/GEOLocationsPopup.vue';
import moment from 'moment';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import * as types from '@/store/project/actionTypes';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import CloudAccountsTab from '@/components/configuration/CloudAccountsTab.vue';
import { LOAD_ALL_ORGANIZATIONS } from '@/store/organization/actionTypes';
import AdvancedFiltersHeader from '@/components/common/AdvancedFiltersHeader.vue';
import { CLEAR_DOMAIN_FILTER } from '@/store/filters/actionTypes';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';

const constantItemsSizesToReduce = {
  addTestCard: 243,
  marginAddTestCard: 10,
  toggleBtn: 90,
  marginLeftToggle: 25,
  marginRightToggle: 12,
  screenLeftSide: 85,
  screenRightSide: 35,
  checkboxWidth: 35,
  checkboxMargin: 5,
  leftBar: 120,
};

const testCardConstantSizes = {
  testCard: 243,
  marginTestCard: 10,
};

const tldConstantSizes = {
  addTestCard: 24,
  leftBar: 120,
  checkboxWidth: 35,
  checkboxMargin: 5,
  marginLeftToggle: 25,
  marginRightToggle: 12,
  toggleBtn: 40,
  screenLeftSide: 85,
  screenRightSide: 35,
  urlColumn: 250,
  validatedColumn: 80,
  sensitivityColumn: 80,
  saasColumn: 150,
  wafColumn: 170,
  riskScore: 150,
  stats: 400,
  menu: 24,
};

export default {
  name: 'ConfigurationLayout',

  components: {
    AdvancedFiltersHeader,
    CloudAccountsTab,
    GEOLocationsPopup,
    GeoLocationsWidget,
    InfostealersKeywordsWidget,
    SuccessOperationMessage,
    AddTagsPopup,
    LeaksTab,
    AddNewItemPopup,
    EmployeesTab,
    CloudAssetsTab,
    InfrastructureTab,
    TopLevelDomainsTab,
    TechnologiesTab,
    InfostealersTab,
    ErrorMessage,
    CompanyLogo,
    YesNoPopup,
  },
  data() {
    return {
      CynergyAppAi,
      CynergyAppAiWhite,
      polling: null,
      activeName: 'topLevelDomain',
      currentName: 'topLevelDomain',
      showPopup: false,
      assetsForTagPopup: [],
      isForCloudAccount: false,
      isForInfrastructure: false,
      isBulkOperation: false,
      showGeoLocationsPopup: false,
      showAddTagsPopup: false,
      windowWidth: window.innerWidth,
      successMessage: '',
      errorMessage: '',
      organizationWatcherCounter: 0,
      forceResetSelectedItemsInfrastructure: false,
      forceLoadInfrastructure: false,
      pollingAddTagsAction: null,
      forceResetSelectedItemsTopLevelDomain: false,
      forceLoadTopLevelDomain: false,
      showTagsApprovalPopup: false,
      payloadForTags: {},
      runningKeywordsForWidget: [],
      // Highest progress per keyword+token, so the widget % never goes backwards.
      keywordProgressMax: {},
    };
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    clearInterval(this.polling);
    clearInterval(this.pollingAddTagsAction);
    // Stop store-based polling
    this.$store.dispatch(STOP_INFOSTEALERS_POLLING);
  },
  async mounted() {
    await this.pollData();
    await this.$store.dispatch(RESET_SUCCESS_OPERATION_MESSAGE);
    window.addEventListener('resize', this.onResize);
    switch (this.$route.hash) {
    case '#employees':
      this.activeName = 'employees';
      break;
    case '#infrastructure':
      this.activeName = 'infrastructure';
      break;
    case '#cloud':
      this.activeName = 'cloud';
      break;
    case '#technologies':
      this.activeName = 'technologies';
      break;
    case '#leaks':
      this.activeName = 'leaks';
      break;
    case '#cloud_accounts':
      this.activeName = 'cloud_accounts';
      break;
    case '#infostealers':
      this.activeName = 'infostealers';
      break;
    default:
      break;
    }
    // Start polling if infostealers tab is active on page load and brand protection is enabled
    if (this.activeName === 'infostealers' && this.brandProtectionFeatureOn) {
      this.startInfostealersPolling();
    }
  },
  async created() {
    if (this.profile) {
      await this.$store.dispatch(CALCULATE_LAST_SCAN_DATA, { domain: this.domainToPass, timeRange: null });
      await this.$store.dispatch(LOAD_ALL_ORGANIZATIONS);
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
      if (this.organizationFilter.length) {
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          sort: {
            configData: 'DESC',
          },
          page: 1,
          limit: this.paginationItemsPerPageByTabs.cloudAccount,
        });
      }
      await this.$store.dispatch(LOAD_DOMAIN_TAGS_FROM_CONFIGURATION);
      await this.$store.dispatch(LOAD_ORGANIZATION_TAGS_FROM_CONFIGURATION);
      await this.$store.dispatch(LOAD_CLOUD_ACCOUNT_TAGS_FROM_CONFIGURATION);
      await this.$store.dispatch(LOAD_AMOUNT_OF_RUNNING_PROJECTS);
    }
  },
  computed: {
    ...mapGetters([
      'successOperationMessage',
      'hasLicenseModule',
      'whiteLabelingConfig',
      'domainFilter',
      'organizationFilter',
      'domains',
      'configurationDomains',
      'configurationCloudAccounts',
      'profile',
      'paginationItemsPerPageByTabs',
      'organizations',
      'companyData',
      'infostealersProgressData',
      'infostealersFilters',
      'systemSettings',
      'topLevelDomains',
      'locationOptions',
      'addTagsRefId',
      'addTagsIntervalRunning',
    ]),
    ...mapGetters('theme', ['isDarkMode']),
    activeAiLogo() {
      return this.isDarkMode ? this.CynergyAppAiWhite : this.CynergyAppAi;
    },
    locationsForIPs() {
      return this.locationOptions;
    },
    timezonesItems() {
      return getGroupedTimezonesList().map(tz => ({
        value: tz.offset,
        name: tz.name,
        id: tz.id,
      }));
    },
    hasFilter() {
      return !!this.organizationFilter.length &&
        !!this.domainFilter.length &&
        this.domainFilter
          .some(asset => this.configurationDomains
            .find(domain => domain.configData === asset) || asset === this.companyData.domain);
    },
    cloudComplianceFeatureOn() {
      return this.hasLicenseModule('cloud_compliance');
    },
    brandProtectionFeatureOn() {
      return this.hasLicenseModule('infostealers');
    },
    sizeOfActionsTLDTab() {
      return (this.windowWidth - Object.values(tldConstantSizes).reduce((a, b) => a + b));
    },
    amountOfTestCardInRow() {
      return Math.trunc((this.windowWidth - Object.values(constantItemsSizesToReduce).reduce((a, b) => a + b)) / (Object.values(testCardConstantSizes).reduce((a, b) => a + b)));
    },
    domainToPass() {
      if (this.domainSelected.length) {
        return this.domainSelected;
      }
      if (this.organizationFilter.length === 1 && this.companyData?.name && this.organizationFilter.includes(this.companyData.name)) {
        return [this.domains[0]];
      }
      return [];
    },
    accountsToPass() {
      return this.accountsSelected.length > 0 ? this.accountsSelected : [];
    },
    listOfDomainsForFilter() {
      const domainsSet = new Set(this.domains);
      this.configurationDomains.forEach(configurationDomain => domainsSet.add(configurationDomain.configData));
      const cloudAccountUrls = this.configurationCloudAccounts.map(cloudAccount => cloudAccount.configData);
      return [...cloudAccountUrls, ...Array.from(domainsSet)];
    },
    validationRules() {
      return {
        ...rules,
        domain: v => rules.newDomainConfiguration(v),
      };
    },
    accountsSelected: {
      get() {
        return this.domainFilter;
      },
    },
    domainSelected: {
      get() {
        return this.domainFilter;
      },
    },
  },
  watch: {
    async profile() {
      if (this.profile) {
        await this.$store.dispatch(CALCULATE_LAST_SCAN_DATA, { domain: this.domainToPass, timeRange: null });
        await this.$store.dispatch(TOGGLE_DOWNLOAD_REPORT_DISABLE, true);
        await this.$store.dispatch(LOAD_DOMAINS);
        await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
        await this.$store.dispatch(LOAD_DOMAIN_TAGS_FROM_CONFIGURATION);
        if (this.organizationFilter.length) {
          await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
            sort: {
              configData: 'DESC',
            },
            page: 1,
            limit: this.paginationItemsPerPageByTabs.cloudAccount,
          });
        }
        await this.$store.dispatch(LOAD_CLOUD_ACCOUNT_TAGS_FROM_CONFIGURATION);
        await this.$store.dispatch(LOAD_ALL_ORGANIZATIONS);
        await this.$store.dispatch(LOAD_ORGANIZATION_TAGS_FROM_CONFIGURATION);
        await this.$store.dispatch(TOGGLE_DOWNLOAD_REPORT_DISABLE, false);
      }
    },
    async organizationFilter() {
      if (this.organizationWatcherCounter > 0) {
        await this.$store.dispatch(CLEAR_DOMAIN_FILTER);
      } else {
        this.organizationWatcherCounter += 1;
      }
      await this.$store.dispatch(CALCULATE_LAST_SCAN_DATA, { domain: this.domainToPass, timeRange: null });
    },
    successOperationMessage() {
      this.successMessage = this.successOperationMessage;
    },
    domainFilter() {
      clearInterval(this.pollingAddTagsAction);
      // Re-scope the running-keywords widget immediately when the domain
      // selection changes (don't wait for the next poll).
      if (this.activeName === 'infostealers') {
        this.updateWidgetData();
      }
    },
    activeName() {
      clearInterval(this.pollingAddTagsAction);
      if (this.activeName === 'infostealers' && this.brandProtectionFeatureOn) {
        this.startInfostealersPolling();
      } else {
        this.stopInfostealersPolling();
      }
    },
    systemSettings: {
      handler(newSettings, oldSettings) {
        // Restart polling when keywords list changes (regardless of current tab) and brand protection is enabled
        if (!this.brandProtectionFeatureOn) {
          return;
        }
        const newKeywords = newSettings?.settingsMetaData?.brandProtection?.keywordsList || [];
        const oldKeywords = oldSettings?.settingsMetaData?.brandProtection?.keywordsList || [];
        // Check if keywords list has changed
        if (JSON.stringify(newKeywords) !== JSON.stringify(oldKeywords)) {
          this.startInfostealersPolling();
        }
      },
      deep: true,
    },
    infostealersProgressData: {
      handler() {
        // Update widget data when progress data changes
        if (this.activeName === 'infostealers') {
          this.updateWidgetData();
        }
      },
      deep: true,
    },
  },
  methods: {
    async pollData() {
      this.polling = setInterval(async () => {
        await this.$store.dispatch(types.LOAD_AMOUNT_OF_RUNNING_PROJECTS);
      }, 30000);
    },
    async startInfostealersPolling() {
      // Only start polling if brand protection feature is enabled
      if (!this.brandProtectionFeatureOn) {
        return;
      }
      // Load system settings in background, don't wait for it (read-only;
      // never a write, so opening the page does not trigger a scan)
      this.$store.dispatch(GET_SYSTEM_SETTINGS);
      await this.$store.dispatch(START_INFOSTEALERS_POLLING);
      this.updateWidgetData();
    },

    stopInfostealersPolling() {
      this.$store.dispatch(STOP_INFOSTEALERS_POLLING);
    },

    updateWidgetData() {
      const runningJobs = this.infostealersProgressData?.running_jobs || [];
      if (runningJobs.length === 0) {
        this.runningKeywordsForWidget = [];
        return;
      }

      // Platform + api + legacy keywords belong in this widget — never the
      // cyn-ai bucket (owned by the agents hub).
      const configured = configuredKeywords(this.systemSettings?.settingsMetaData?.brandProtection);

      // The widget is decoupled from the domain selection: it shows every
      // configured (manual-configuration) keyword's status, running or not,
      // regardless of which domain(s) are currently selected.
      const filteredRunningJobs = runningJobs.filter(job => configured.has(job.keyword));

      // Show actively-running (pending/running) scans above finished ones so a
      // live scan is always visible at the top of the widget. Stable within each
      // group (preserves the backend order).
      // Running/pending keywords first, finished ones after.
      const activeRank = job => (['pending', 'running'].includes(job.status) ? 0 : 1);
      const sortedRunningJobs = [...filteredRunningJobs].sort(
        (a, b) => activeRank(a) - activeRank(b),
      );

      this.runningKeywordsForWidget = sortedRunningJobs.map(job => {
        const clampedValue = this.monotonicProgressValue(job);
        return {
          name: job.keyword,
          state: calculateState(job),
          progressPercentage: `${clampedValue}%`,
          progressValue: clampedValue,
          amount: calculateAmount(job),
        };
      });
    },
    // Non-decreasing progress per scan token (raw Splunk progress can dip).
    monotonicProgressValue(job) {
      const raw = calculateProgressValue(job);
      const previous = this.keywordProgressMax[job.keyword];
      const value = previous && previous.token === job.token
        ? Math.max(previous.value, raw)
        : raw;
      this.keywordProgressMax[job.keyword] = { token: job.token, value };
      return value;
    },
    openGeoLocationsPopup() {
      this.showGeoLocationsPopup = !this.showGeoLocationsPopup;
    },
    async clearSuccessMessage() {
      await this.$store.dispatch(RESET_SUCCESS_OPERATION_MESSAGE);
    },
    toggleTagsPopup(payload) {
      this.isForCloudAccount = (this.activeName === 'cloud_accounts');
      this.isForInfrastructure = (this.activeName === 'infrastructure');
      this.isBulkOperation = payload?.isAllSelected || payload?.selected?.length;
      this.assetsForTagPopup = payload;
      this.showAddTagsPopup = true;
    },
    closeTagsPopup() {
      this.showAddTagsPopup = false;
    },
    forceRerenderTopLevelDomainTab(value) {
      this.forceLoadTopLevelDomain = value;
    },
    async onAddTagsClickYesNoButton() {
      this.showTagsApprovalPopup = false;
      this.isForCloudAccount = false;
      this.isForInfrastructure = false;
      this.isBulkOperation = false;
      this.assetsForTagPopup = [];
    },
    async submitTagsInformation(payload) {
      let configData = [];
      if (payload.configType === CONFIGURATION.CONFIG_TYPES.TAGS_DOMAIN) {
        this.forceLoadTopLevelDomain = false;
        this.forceResetSelectedItemsTopLevelDomain = false;
      } else if (payload.configType === CONFIGURATION.CONFIG_TYPES.TAGS_SUBDOMAIN || payload.configType === CONFIGURATION.CONFIG_TYPES.TAGS_AUTO) {
        this.forceLoadInfrastructure = false;
        this.forceResetSelectedItemsInfrastructure = false;
      }
      if (this.isBulkOperation) {
        configData = {
          selected: payload.items.payload.selected,
          excluded: payload.items.payload.excluded,
          tags: payload.items.tags,
          search: payload.items.payload.search,
          filter: payload.items.payload.filter,
          toggles: payload.items.payload.toggles,
          isAllSelected: payload.items.payload.isAllSelected,
          domain: this.domainFilter || [],
          organizations: this.organizationFilter || [],
          tab: this.activeName,
        };
      } else {
        configData = payload.items.map(item => ({
          data: item.url,
          metaData: item.metaData || [],
          // eslint-disable-next-line no-nested-ternary
          tld: item.tld ? item.tld : (payload.configType === CONFIGURATION.CONFIG_TYPES.TAGS_DOMAIN ? item.url : null),
          subdomain: item.subdomain || null,
          tags: item.tags,
        }));
      }
      clearInterval(this.pollingAddTagsAction);
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: configData,
        configType: payload.configType,
        scanEmployees: false,
        successMessageType: payload.action || CONFIGURATION_ACTION_TYPES.ADD_TAGS,
      });
      if (this.addTagsRefId && this.addTagsRefId !== '') {
        this.showTagsApprovalPopup = true;
        this.pollingAddTagsAction = setInterval(async () => {
          if (!this.addTagsRefId || this.addTagsRefId === '') {
            if (payload.configType === CONFIGURATION.CONFIG_TYPES.TAGS_SUBDOMAIN) {
              this.forceLoadInfrastructure = true;
              this.forceResetSelectedItemsInfrastructure = true;
            } else if (payload.configType === CONFIGURATION.CONFIG_TYPES.TAGS_DOMAIN) {
              this.forceLoadTopLevelDomain = true;
              this.forceResetSelectedItemsTopLevelDomain = true;
            }
            clearInterval(this.pollingAddTagsAction);
          } else {
            if (this.addTagsRefId !== '') {
              configData.refId = this.addTagsRefId;
            }
            if (!this.addTagsIntervalRunning) {
              await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
                payload: configData,
                configType: payload.configType,
                scanEmployees: false,
                successMessageType: payload.action || CONFIGURATION_ACTION_TYPES.ADD_TAGS,
              });
            }
          }
          delete configData.refId;
        }, 1000);
      }
      if (this.addTagsRefId === '') {
        if (payload.configType === CONFIGURATION.CONFIG_TYPES.TAGS_DOMAIN) {
          this.forceLoadTopLevelDomain = true;
          this.forceResetSelectedItemsTopLevelDomain = true;
        } else if (payload.configType === CONFIGURATION.CONFIG_TYPES.TAGS_SUBDOMAIN || payload.configType === CONFIGURATION.CONFIG_TYPES.TAGS_AUTO) {
          this.forceLoadInfrastructure = true;
          this.forceResetSelectedItemsInfrastructure = true;
        }
      }
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    async apply() {
      await this.$store.dispatch(CALCULATE_LAST_SCAN_DATA, { domain: this.domainToPass, timeRange: null, initiatedByFilterApply: true });
    },
    // eslint-disable-next-line no-unused-vars
    handleClick(tab, event) {
    },
    dispatch(e) {
      this.$emit('input', e);
      this.domainSelected = e;
    },
    calculateTimeZone() {
      const defaultBrowserTimeZoneValue = String(moment().utcOffset());
      const defaultBrowserTimeZoneObj = this.timezonesItems.find(timeZoneObj => timeZoneObj.value === defaultBrowserTimeZoneValue);
      return defaultBrowserTimeZoneObj?.id || null;
    },
    async addNewDomains(payload) {
      const { items, scanEmployees } = payload;
      const timezone = this.calculateTimeZone();
      const configData = items.map(item => ({
        data: item.url,
        metaData: [],
        tld: item.tld,
        tags: [],
        ...item,
      }));
      try {
        await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
          payload: configData,
          configType: CONFIGURATION.CONFIG_TYPES.DOMAIN,
          scanEmployees,
          successMessageType: CONFIGURATION_ACTION_TYPES.ADD_DOMAIN,
          displayMessage: true,
          timezone,
        });
      } catch (e) {
        this.errorMessage = e.message;
      }
      await this.$store.dispatch(LOAD_DOMAINS);
      this.forceLoadTopLevelDomain = true;
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        sort: {
          configData: 'DESC',
        },
        page: 1,
        limit: this.paginationItemsPerPageByTabs.cloudAccount,
      });
      await this.$store.dispatch(LOAD_DOMAIN_TAGS_FROM_CONFIGURATION);
      await this.$store.dispatch(LOAD_ORGANIZATION_TAGS_FROM_CONFIGURATION);
      await this.$store.dispatch(LOAD_CLOUD_ACCOUNT_TAGS_FROM_CONFIGURATION);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.success-operation-message {
  color: var(--brand-primary);
  font-size: $font-size-sm;
  line-height: $font-size-base;
  min-height: $font-size-base;
  margin-bottom: $space-2;
  text-align: center;
}

.configuration-layout {
  background: var(--bg-primary);

  .top-section {
    margin-top: rem(100);
    margin-left: $space-8;
    margin-bottom: rem(-100);

    .logo-ai {
      height: rem(65);
      display: flex;
      flex-direction: row;
      margin-right: $space-8;
      align-items: center;
      justify-content: flex-end;
      margin-top: rem(-100);
      flex-grow: 1;
      flex-shrink: 1;
      margin-bottom: $space-9;

      &__wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      &__logo {
        width: rem(100);
        height: auto;
      }

      &__text {
        font-family: $font-family-semibold;
        font-size: $font-size-2xs;
        color: var(--text-secondary);
      }
    }

    .widget-section {
      min-height: $widget-min-height;
      display: flex;
      flex-grow: 1;
      flex-shrink: 1;
      justify-content: right;
      margin-right: $space-4;
      margin-top: rem(-90);
      padding-bottom: rem(55);
      margin-bottom: $space-3;
    }
  }

  .tabs-section {
    margin-top: rem(-65);
  }

  // Element UI Tab overrides - using :deep() for scoped styles
  :deep(.el-tabs__item) {
    height: $tab-height;
    color: var(--text-primary);
    font-family: $font-family-medium;
    font-size: $font-size-md;
    letter-spacing: 0;
    line-height: $tab-height;
    opacity: 0.7;

    &:hover {
      color: var(--text-primary);
      opacity: 1;
    }

    &.is-active {
      color: var(--text-primary);
      opacity: 1;
    }
  }

  :deep(.el-tabs__header) {
    z-index: 0;
  }

  :deep(.el-tabs__nav-scroll) {
    margin-left: $space-8;
  }

  :deep(.el-tabs__nav-wrap::after) {
    height: 1px;
    background-color: var(--border-primary);
  }

  // Vuetify input overrides
  :deep(.v-text-field--outlined fieldset) {
    border-color: var(--border-primary);
  }

  :deep(.v-input__slot:hover fieldset) {
    border-color: var(--border-hover);
  }

  // Table text and icons
  :deep(.tool-tip-url .url-section .url),
  :deep(.tool-tip-url .url-section .url div) {
    color: var(--text-primary);
  }

  :deep(.more-btn),
  :deep(.more-btn),
  :deep(.open-btn .v-icon),
  :deep(.subdomain-data .v-icon),
  :deep(.tech-data .v-icon),
  :deep(.cloud-data .v-icon) {
    color: var(--text-primary);
  }

  // Leak URL open button uses its own brand color — restore it after the broad override above
  :deep(.leak-url-table-cell .open-btn .v-icon) {
    color: $action-button-text-color;
  }

  // Secondary text
  :deep(.configuration-item__last-scanned),
  :deep(.no-tags),
  :deep(.local_offer_icon) {
    color: var(--text-secondary);
  }

  // Filtered search
  :deep(.filtered-search__input) {
    input {
      color: var(--text-primary);

      &::placeholder {
        color: var(--text-secondary);
      }
    }

    .v-icon {
      color: var(--text-primary);
    }
  }

  // Switch labels
  :deep(.v-input--switch .v-label),
  :deep(.theme--light.v-label) {
    color: var(--text-primary);
    opacity: 1;
  }

  // Headers
  :deep(.header) {
    color: var(--text-primary);
  }

  // Select all label
  :deep(.select-all__label) {
    color: var(--text-primary);
    opacity: 1;
  }

  // Pagination labels
  :deep(.advanced-pagination__items_amount_options__label) {
    color: var(--text-primary);
  }

  // No data placeholder
  :deep(.no-data__header__title) {
    color: var(--text-primary);
  }

  :deep(.no-data__header__message) {
    color: var(--text-secondary);
  }

  // Element UI radio buttons
  :deep(.el-radio-button__inner) {
    background-color: transparent;
    color: var(--text-secondary);
    border-color: var(--border-primary);

    &:hover {
      color: var(--text-primary);
    }
  }

  :deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
    color: $white;
    background-color: var(--brand-primary);
    box-shadow: -1px 0 0 0 var(--brand-primary);
  }

  // Row components
  :deep(.cloud-account-row),
  :deep(.infrastructure-item-row),
  :deep(.cloud-asset-row),
  :deep(.cloud-services-asset-row) {
    .url,
    .name,
    .type,
    .label,
    .asset-name {
      color: var(--text-primary);
    }

    .url__data__tooltip {
      color: var(--text-primary);
    }

    // Icons (except success state)
    .material-icons-round:not(.successful),
    .material-icons:not(.successful) {
      color: var(--text-primary);
    }

    .successful {
      opacity: 1;
    }

    // WAF icons
    .configuration-item__waf__half,
    .configuration-item__waf__values__part1,
    .configuration-item__waf__values__part2 {
      color: var(--text-primary);
      fill: var(--text-primary);

      path {
        fill: var(--text-primary);
      }
    }

    // All icons in rows
    .v-icon,
    .material-icons,
    .material-icons-outlined,
    .material-icons-round,
    .expand-icon {
      &:not(.successful) {
        color: var(--text-primary);
      }
    }
  }
}

// Dark theme enhancements (scoped)
[data-theme="dark"] {
  .configuration-layout {
    :deep(.el-tabs__item) {
      opacity: 0.85;

      &.is-active {
        color: $white;
        font-weight: 700;
      }

      &:hover {
        opacity: 1;
        color: $white;
      }
    }

    :deep(.v-text-field--outlined fieldset) {
      border-color: rgba(255, 255, 255, 0.2);
    }

    :deep(.v-input__slot:hover fieldset) {
      border-color: rgba(255, 255, 255, 0.5);
    }

    :deep(.el-radio-button__inner) {
      border-color: rgba(255, 255, 255, 0.2);
    }
  }
}
</style>
